import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Calendar, Users, Activity, MessageSquare,
  Settings, LogOut, Menu, X, Bell, ChevronDown, Stethoscope
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const NAV = [
  { to: '/',            icon: LayoutDashboard, key: 'nav.dashboard',    end: true },
  { to: '/appointments',icon: Calendar,        key: 'nav.appointments' },
  { to: '/patients',    icon: Users,           key: 'nav.patients' },
  { to: '/followup',    icon: Activity,        key: 'nav.followup' },
  { to: '/messages',    icon: MessageSquare,   key: 'nav.messages' },
  { to: '/settings',    icon: Settings,        key: 'nav.settings' },
];

export default function DashboardLayout() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAr = i18n.language === 'ar';
  const [open, setOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 ${isAr ? 'right-0' : 'left-0'} z-50 w-64 bg-sidebar flex flex-col transition-transform duration-300
        ${open ? 'translate-x-0' : (isAr ? 'translate-x-full lg:translate-x-0' : '-translate-x-full lg:translate-x-0')}`}>

        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-6 border-b border-white/10">
          <div className="w-10 h-10 bg-sky-600 rounded-2xl flex items-center justify-center">
            <Stethoscope size={22} className="text-white" />
          </div>
          <div>
            <p className="text-white font-black text-lg leading-none">{isAr ? 'طبيبي' : 'DoctorBot'}</p>
            <p className="text-slate-400 text-xs">{isAr ? 'لوحة الطبيب' : 'Doctor Portal'}</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map(({ to, icon: Icon, key, end }) => (
            <NavLink key={to} to={to} end={end}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Icon size={18} />
              <span>{t(key)}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Info */}
        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="w-9 h-9 rounded-xl bg-sky-600/30 flex items-center justify-center">
              <span className="text-sky-400 text-sm font-bold">{user?.fullNameAr?.charAt(0) || 'د'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-semibold truncate">{user?.fullNameAr || user?.full_name_ar}</p>
              <p className="text-slate-500 text-[10px]">{user?.phone}</p>
            </div>
          </div>
          <button onClick={() => { logout(); navigate('/login'); }}
            className="sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-500/10">
            <LogOut size={16} /> {t('auth.logout')}
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {open && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className={`flex-1 flex flex-col ${isAr ? 'lg:me-64' : 'lg:ms-64'} min-w-0`}>
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-100 px-4 lg:px-6 h-14 flex items-center justify-between shrink-0">
          <button onClick={() => setOpen(!open)} className="lg:hidden p-2 rounded-xl hover:bg-gray-100">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <button onClick={() => i18n.changeLanguage(isAr ? 'en' : 'ar')}
              className="px-2.5 py-1 text-xs font-bold text-sky-600 bg-sky-50 rounded-lg hover:bg-sky-100 transition-colors">
              {isAr ? 'EN' : 'ع'}
            </button>
            <button className="relative p-2 rounded-xl hover:bg-gray-100">
              <Bell size={18} className="text-gray-500" />
            </button>
            <button className="flex items-center gap-2 px-3 py-1.5 rounded-xl hover:bg-gray-100 transition-colors">
              <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center">
                <span className="text-sky-700 text-xs font-bold">{user?.fullNameAr?.charAt(0) || 'د'}</span>
              </div>
              <span className="text-sm font-semibold text-gray-700 hidden sm:block">{user?.fullNameAr || 'الطبيب'}</span>
              <ChevronDown size={14} className="text-gray-400" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
